import { useState } from "react";
import { Link } from "react-router-dom";
import { featuredProjects } from "@/mocks/projects";

function StarRating({ rating }: { rating: number | null }) {
  const r = rating ?? 0;
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <i
          key={star}
          className={`fas fa-star text-xs ${
            star <= r
              ? "text-yellow-400"
              : star - 0.5 <= r
                ? "text-yellow-400"
                : "text-gray-300"
          }`}
        />
      ))}
    </div>
  );
}

export default function ProjectsSection() {
  const [activePage, setActivePage] = useState(1);
  const totalPages = 183;

  const handlePageClick = (page: number) => {
    setActivePage(page);
    document.getElementById("projects-section")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="projects-section" className="py-12 md:py-16 bg-[#0d1117]">
      <div className="w-full px-4 md:px-8 max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-2xl md:text-3xl font-light text-gray-200">
              Looking for Available Projects?
            </h2>
            <div className="mt-3 inline-block border-b-2 border-[#5bc0de] pb-1">
              <span className="text-sm font-semibold text-gray-300">
                Featured projects
              </span>
            </div>
          </div>
          <Link
            to="/post-project"
            className="inline-flex items-center gap-2 bg-[#5cb85c] hover:bg-[#4cae4c] text-white text-sm font-semibold px-5 py-2 rounded transition-all whitespace-nowrap"
          >
            <i className="fas fa-plus-circle" />
            Post a Project
          </Link>
        </div>

        <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 border-b border-gray-700 text-xs font-semibold text-gray-400 uppercase tracking-wider">
          <div className="col-span-5">Project Title</div>
          <div className="col-span-2">By</div>
          <div className="col-span-2">Posted Date</div>
          <div className="col-span-1">Budget</div>
          <div className="col-span-2"></div>
        </div>

        <ul className="divide-y divide-gray-100">
          {featuredProjects.map((project) => (
            <li key={project.id} className="py-4 hover:bg-[#161b22] transition-colors">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-4 items-center">
                <div className="md:col-span-5 flex items-center gap-3 min-w-0">
                  <img
                    src={project.authorAvatar}
                    alt={project.author}
                    className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                  />
                  <Link
                    to={`/project/${project.id}`}
                    className="text-sm text-gray-200 hover:text-[#5bc0de] truncate font-medium"
                    title={project.title}
                  >
                    {project.title}
                  </Link>
                </div>

                <div className="md:col-span-2 hidden md:block">
                  <span className="text-sm text-gray-400">{project.author}</span>
                </div>

                <div className="md:col-span-2 hidden md:block">
                  <span className="text-sm text-gray-400">{project.date}</span>
                </div>

                <div className="md:col-span-1 hidden md:block">
                  <span className="text-sm font-semibold text-gray-300">{project.budget}</span>
                </div>

                <div className="md:col-span-2 flex justify-start md:justify-end">
                  <Link
                    to={`/project/${project.id}`}
                    className="inline-block bg-[#5cb85c] hover:bg-[#4cae4c] text-white text-xs font-semibold px-4 py-1.5 rounded transition-all whitespace-nowrap"
                  >
                    View
                  </Link>
                </div>

                <div className="md:hidden flex items-center gap-4 text-xs text-gray-400">
                  <span>By {project.author}</span>
                  <span>{project.date}</span>
                  <span className="font-semibold text-gray-300">{project.budget}</span>
                </div>
              </div>
            </li>
          ))}
        </ul>

        <div className="flex items-center justify-center gap-2 mt-8">
          {[1, 2, 3].map((p) => (
            <button
              key={p}
              onClick={() => handlePageClick(p)}
              className={`w-8 h-8 flex items-center justify-center rounded text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                activePage === p ? "bg-[#5bc0de] text-white" : "text-gray-400 hover:bg-[#1c2128]"
              }`}
            >
              {p}
            </button>
          ))}
          <span className="text-sm text-gray-400">...</span>
          <button
            onClick={() => handlePageClick(totalPages)}
            className={`w-8 h-8 flex items-center justify-center rounded text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
              activePage === totalPages ? "bg-[#5bc0de] text-white" : "text-gray-400 hover:bg-[#1c2128]"
            }`}
          >
            {totalPages}
          </button>
          <button
            onClick={() => handlePageClick(Math.min(activePage + 1, totalPages))}
            className="w-8 h-8 flex items-center justify-center rounded text-sm text-gray-400 hover:bg-[#1c2128] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="fas fa-angle-double-right" />
          </button>
        </div>
      </div>
    </section>
  );
}